import React from 'react';
import { motion } from 'framer-motion';
import PageHero from '../components/PageHero';
import useInView from '../hooks/useInView';
import { BOARD_EMAIL } from '../config';
import usePageMeta from '../hooks/usePageMeta';
import styles from './About.module.css';

export default function About() {
  usePageMeta(
    'About — Revive Worship',
    'Revive is a Pittsburgh-based worship ministry advancing the Great Commission through worship in unlikely places.'
  );

  const [proseRef, proseIn] = useInView({ threshold: 0.15 });
  const [boardRef, boardIn] = useInView({ threshold: 0.2 });

  return (
    <>
      <PageHero
        eyebrow="About"
        title={['Advancing the', <em key="gc">Great Commission</em>, 'through worship.']}
        lede="Revive is a Pittsburgh-based worship ministry that seeks after the Father's heart. We exist to empower and equip all people to give Him the worship He is due."
        image="/images/about.jpg"
      />

      <section className={styles.story}>
        <div className={styles.shell}>
          <div className={styles.proseGrid} ref={proseRef}>
            <aside className={styles.proseAside}>
              <p className={styles.proseAsideLabel}>Our Story</p>
              <p className={styles.proseAsideText}>
                Founded May 2025. Independent worship ministry. Pittsburgh, PA.
              </p>
            </aside>

            <div className={`${styles.prose} ${proseIn ? styles.proseIn : ''}`}>
              <p>
                Revive Worship was founded in May 2025 with a single conviction:
                that there is no place too unlikely for worship. We were born out
                of a vision to bring worship into the spaces that do not yet
                worship, drawing all people into deeper intimacy with God.
              </p>
              <p>
                Since our founding, we have held monthly worship nights across a
                wide range of spaces: homes, parks, university campuses, co-working
                hubs, and any room that opens its doors. Our desire is to proclaim
                Jesus to all people, and our model fosters spaces where churches,
                people, and many expressions of worship come together, reflective
                of the <em>Acts 2 church</em> and the Church Christ envisioned.
              </p>
              <p>
                We are not a church, nor are we affiliated with any one local
                church. We are a growing community of all kinds of people who love
                God and love each other. Everyone, regardless of age, background,
                or belief, is welcomed.
              </p>
              <p className={styles.creed}>We exist to <em>worship.</em></p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.boardBlock} ref={boardRef}>
        <div className={styles.shell}>
          <div className={`${styles.boardGrid} ${boardIn ? styles.boardIn : ''}`}>
            <div className={styles.boardLeft}>
              <p className={styles.proseAsideLabel}>Leadership</p>
              <h2 className={styles.boardTitle}>
                <span>Led from the inside.</span>
                <span>Accountable on the outside.</span>
              </h2>
            </div>

            <div className={styles.boardRight}>
              <p>
                Revive is internally led by a core team of leaders who attend
                to the day-to-day operational aspects of the organization,
                including but not limited to worship, production, ministry,
                media, and operations.
              </p>
              <p>
                That team is held accountable by a Board of Directors made up
                of trusted leaders with a demonstrated track record of faithful,
                healthy leadership at home and in the church. While they do not
                lead the day to day, we approach them for counsel and oversight
                on what matters most.
              </p>
              <p>
                Have a question for our board, or want to bring something to
                their attention?
              </p>

              <motion.a
                className={styles.boardCta}
                href={`mailto:${BOARD_EMAIL}`}
                whileHover={{ x: 4 }}
                transition={{ duration: 0.3 }}
              >
                Contact Revive's Board
                <span aria-hidden="true">→</span>
              </motion.a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
